// Display-equation renderer with per-symbol hover (PRD 07 §10).
// KaTeX with trust:true lets \htmlId{<sid>}{...} emit stable DOM ids
// per symbol so the hover hook can light up the matching tooltip row.

import { useEffect, useMemo, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

import type { Definition, EquationEntry, EquationSymbol } from "../data/types";
import { useEquationHover } from "../reading/useEquationHover";
import { annotateLatex } from "./annotateLatex";
import type { CanonicalResolver } from "./canonicalResolver";
import { symbolKey, useEquationSymbolHover } from "./equationSymbolHover";

interface EquationBlockProps {
  equation: EquationEntry;
  docId?: string;
  canonicalResolver?: CanonicalResolver;
}

export function EquationBlock({
  equation, docId, canonicalResolver,
}: EquationBlockProps) {
  const annotated = useMemo(
    () => annotateLatex(equation.latex, equation.symbols),
    [equation.latex, equation.symbols],
  );
  const html = useMemo(
    () =>
      katex.renderToString(annotated, {
        displayMode: true,
        throwOnError: false,
        trust: (ctx) =>
          ctx.command === "\\htmlId" ||
          ctx.command === "\\htmlClass" ||
          ctx.command === "\\htmlData",
        strict: "ignore",
      }),
    [annotated],
  );

  const [showPanel, setShowPanel] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const symbolIds = useMemo(
    () => equation.symbols.map((s) => s.symbol_id),
    [equation.symbols],
  );
  const hoveredSymbolId = useEquationHover(wrapRef, symbolIds);

  // PRD 10 Phase B deliverable 3 — the hover is doc-level, not block-level.
  // This block reports the symbol under its own pointer to the coordinator,
  // then lights whatever key the coordinator says is active, even when the
  // pointer is in a different equation. Matches the widget's crossSymbol.ts.
  const { hoveredKey, publish } = useEquationSymbolHover();
  const keyBySymbolId = useMemo(
    () => new Map(equation.symbols.map((s) => [s.symbol_id, symbolKey(s)])),
    [equation.symbols],
  );
  const localKey = hoveredSymbolId
    ? keyBySymbolId.get(hoveredSymbolId) ?? null
    : null;
  useEffect(() => {
    publish(equation.equation_id, localKey);
  }, [publish, equation.equation_id, localKey]);
  // Falls back to this block's own hover when no provider is mounted, so a
  // standalone EquationBlock keeps working.
  const activeKey = hoveredKey ?? localKey;
  const litSymbolIds = useMemo(
    () =>
      activeKey === null
        ? []
        : equation.symbols
            .filter((s) => symbolKey(s) === activeKey)
            .map((s) => s.symbol_id),
    [equation.symbols, activeKey],
  );
  // KaTeX emits one node per `\htmlId{<symbol_id>}{…}`; those nodes live
  // inside dangerouslySetInnerHTML, so the lit class is toggled directly
  // rather than through the reconciler — same approach as the widget.
  useEffect(() => {
    const root = wrapRef.current;
    if (!root) return;
    const lit = litSymbolIds
      .map((id) => root.querySelector(`[id="${id}"]`))
      .filter((el): el is Element => el !== null);
    for (const el of lit) el.classList.add("maisight-sym-lit");
    return () => {
      for (const el of lit) el.classList.remove("maisight-sym-lit");
    };
  }, [litSymbolIds, html]);

  // PRD 10 Phase B — visible signal when extraction couldn't ground a
  // symbol. data-grounding="ungrounded" lets reviewer-facing CSS flag
  // the equation for attention and gives the battery a queryable hook.
  const hasUngrounded = equation.symbols.some(
    (s) => s.grounding === "ungrounded",
  );

  return (
    <div
      ref={wrapRef}
      className="maisight-equation-block"
      data-equation-id={equation.equation_id}
      data-block-id={equation.block_id}
      data-hovered-symbol={activeKey ?? ""}
      data-glossary-refs={(equation.glossary_refs ?? []).join(" ") || undefined}
      data-grounding={hasUngrounded ? "ungrounded" : undefined}
      onMouseEnter={() => setShowPanel(true)}
      onMouseLeave={() => setShowPanel(false)}
    >
      <div
        className="maisight-equation-math"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showPanel && (
        <EquationPanel
          equation={equation}
          litSymbolIds={litSymbolIds}
          docId={docId}
          canonicalResolver={canonicalResolver}
        />
      )}
    </div>
  );
}

interface PanelProps {
  equation: EquationEntry;
  litSymbolIds: ReadonlyArray<string>;
  docId?: string;
  canonicalResolver?: CanonicalResolver;
}

function EquationPanel({
  equation, litSymbolIds, docId, canonicalResolver,
}: PanelProps) {
  return (
    <aside className="maisight-equation-panel" role="tooltip">
      {equation.summary && (
        <header>
          <DefinitionView definition={equation.summary} />
          {equation.interactions && (
            <p className="maisight-equation-interactions">{equation.interactions}</p>
          )}
        </header>
      )}
      <ul className="maisight-equation-symbols">
        {equation.symbols.map((sym) => (
          <li
            key={sym.symbol_id}
            data-symbol-id={sym.symbol_id}
            className={litSymbolIds.includes(sym.symbol_id) ? "is-lit" : ""}
          >
            <SymbolRow
              symbol={sym}
              docId={docId}
              canonicalResolver={canonicalResolver}
            />
          </li>
        ))}
      </ul>
    </aside>
  );
}

function SymbolRow({
  symbol, docId, canonicalResolver,
}: {
  symbol: EquationSymbol;
  docId?: string;
  canonicalResolver?: CanonicalResolver;
}) {
  // Canonical Definition wins when Pass 2 has merged this symbol across
  // docs (PRD 07 §6.5). Falls back to the per-doc definition.
  const canonical =
    docId && canonicalResolver
      ? canonicalResolver.byRef(docId, symbol.symbol_id)
      : null;
  const definition = canonical?.definition ?? symbol.definition ?? null;
  return (
    <div data-canonical-entity-id={canonical?.entity_id ?? undefined}>
      <code className="maisight-equation-symbol-latex">{symbol.latex}</code>
      {canonical ? (
        <span
          className="maisight-equation-canonical-badge"
          title={`Canonical across ${canonical.member_refs.length} docs in this corpus`}
        >
          ↘
        </span>
      ) : null}
      {symbol.grounding === "ungrounded" && (
        <span className="maisight-equation-warning" title="not defined in surrounding prose">
          ⚠
        </span>
      )}
      {definition ? (
        <DefinitionView definition={definition} />
      ) : (
        <span className="maisight-equation-stub">(no definition yet)</span>
      )}
      {definition?.prerequisites && definition.prerequisites.length > 0 && (
        <p className="maisight-equation-prereqs">
          Builds on:{" "}
          {definition.prerequisites.map((p, i) => (
            <span key={i} className="maisight-equation-prereq-chip">{p}</span>
          ))}
        </p>
      )}
    </div>
  );
}

function DefinitionView({ definition }: { definition: Definition }) {
  return (
    <div className="maisight-definition">
      <p className="maisight-definition-plain">{definition.definition_plain}</p>
      {definition.definition_precise &&
        definition.definition_precise !== definition.definition_plain && (
          <details className="maisight-definition-precise">
            <summary>show precise</summary>
            <p>{definition.definition_precise}</p>
          </details>
        )}
    </div>
  );
}
